import React from 'react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { is } from '@gov-nx/core/types';
import type { PageCode, useAddressLookup } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	FormInput,
	FormRadio,
	FormWidthElementLayout,
	LayoutGap,
} from '@gov-nx/ui/web';

interface ContactAddressFormProps {
	code: PageCode;
	addressLookup: ReturnType<typeof useAddressLookup>;
}

export const ContactAddressForm = ({
	code,
	addressLookup,
}: ContactAddressFormProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();

	const tsn = getLocalizeCurried(code);
	const isProcessing = addressLookup.isLoading;

	return (
		<GovFormWeb
			formDefinition={addressLookup.formDefinition}
			onSubmit={addressLookup.onSubmit}>
			<FormWidthElementLayout type="l">
				{addressLookup.error && <div>{addressLookup.error.message}</div>}

				<LayoutGap gap="m">
					<FormInput
						label={{ children: tsn('formular.pole.nazev-obce') }}
						field={{
							name: 'nazevObce',
							isDisabled: isProcessing,
							placeholder: tsn('formular.placeholder.nazev-obce'),
						}}
					/>

					<FormInput
						label={{ children: tsn('formular.pole.nazev-ulice') }}
						field={{
							name: 'nazevUlice',
							isDisabled: isProcessing,
							placeholder: tsn('formular.placeholder.nazev-ulice'),
						}}
					/>

					<FormInput
						label={{ children: tsn('formular.pole.cislo') }}
						field={{
							name: 'cislo',
							isDisabled: isProcessing,
							placeholder: tsn('formular.placeholder.cislo'),
						}}
					/>

					{addressLookup.results?.length && (
						<FormRadio
							field={{
								name: '_address',
								isDisabled: isProcessing,
								options: addressLookup.results
									.map((option) =>
										option.kod
											? {
													value: option.kod,
													label: { children: option.adresaText ?? '' },
												}
											: undefined
									)
									.filter(is),
							}}
							label={{
								children: tsn('formular.pole.clen-adresa'),
							}}
						/>
					)}
				</LayoutGap>
			</FormWidthElementLayout>

			<ButtonGroupLayout customClasses="mt-(--spacing-m)!">
				<Button
					color="primary"
					type="solid"
					onClick={addressLookup.onLookup}
					aria-label={tsn('akce.wcag.hledat')}>
					{tsn('formular.tlacitko.hledat')}
				</Button>

				<Button
					isLoading={isProcessing}
					nativeType="submit"
					aria-label={tsn('formular.tlacitko.adresa-ulozit')}
					isDisabled={
						isProcessing ||
						!addressLookup.formDefinition.formMethods.watch('_address')
					}
					color="primary"
					type="outlined">
					{tsn('formular.tlacitko.adresa-ulozit')}
				</Button>
			</ButtonGroupLayout>
		</GovFormWeb>
	);
};
